import {
  DatabaseAuto,
  IAnalyticsResultExpense,
  IbulkDelete,
  IbulkDeleteSuccessResponse,
  IcommerceUser,
  IdataArrayResponse,
  IdeleteOneResponse,
  Iexpense, IfilterProps, TexpenseRequest,
  validateData
} from '@biz-nest/core-client';
import { lastValueFrom } from 'rxjs';
import { CommerceClient } from '../../commerce-client';
import { Item } from '../item-related/item.define';

type TexpenseProps = Iexpense<number, IcommerceUser>;

/**
 * Represents an expense record in the system.
 * This class provides methods to manage and track expenses, including creation,
 * updating, and querying expense records with various filtering options.
 *
 * @extends DatabaseAuto - Provides common database operation methods
 * @template TexpenseProps - The type definition for expense properties
 * @public
 * @remarks
 * This is the raw DTO shape as returned by the API. Prefer using the
 * {@link Expense} class when invoking client operations.
 *
 * @usage
 * ```typescript
 * const expense = await Expense.getAll();
 *
 * const expense = await Expense.filterAll({
 *
 * });
 *
 * const expense = await Expense.bulkDelete({
 *
 * });
 *
 * const expense = await Expense.getOne('resourceId');
 *
 * const expense = await Expense.remove();
 *
 * ```
 */
export class Expense extends DatabaseAuto {
  /** Base API endpoint for expense operations */
  private static readonly baseUrl = '/expense';

  /** Unique reference ID for the expense */
  readonly urId: TexpenseProps['urId'];

  /** Company associated with the expense */
  readonly company: TexpenseProps['company'];

  /** Name or description of the expense */
  readonly name: TexpenseProps['name'];

  /** User who created the expense */
  readonly commerceUser: TexpenseProps['commerceUser'];

  /** Cost amount of the expense */
  readonly cost: TexpenseProps['cost'];

  /** Category of the expense */
  readonly category: TexpenseProps['category'];

  /** Additional notes about the expense */
  readonly note: string;

  /** List of items associated with the expense */
  readonly items: Item[] = [];

  /** Type of the expense (e.g., 'business', 'personal') */
  readonly expenseType: Iexpense['expenseType'];

  /**
   * Creates a new instance of Expense.
   * @param {TexpenseProps} data - The expense data used to initialize the instance
   * @throws {Error} If the data is invalid or missing required fields
   */
  constructor(data: TexpenseProps) {
    validateData(data, {
      urId: {
        validate: (v) =>
          typeof v === 'string' &&
          v.trim().length > 0,
        message: 'Unique reference ID must be a non-empty string'
      },
      name: {
        validate: (v) =>
          typeof v === 'string' &&
          v.trim().length > 0,
        message: 'Expense name must be a non-empty string'
      },
      commerceUser: {
        validate: (v) =>
          typeof v === 'object' &&
          v._id.trim().length > 0,
        message: 'Added by name must be a valid user object'
      },
      cost: {
        validate: (v) =>
          typeof v === 'number' &&
          v >= 0,
        message: 'Cost must be a non-negative number'
      },
      company: {
        required: false,
        validate: (v) =>
          v === undefined ||
          (typeof v === 'string' && v.trim().length > 0),
        message: 'Company ID must be a non-empty string if provided'
      }
    });

    super(data);

    this.urId = data.urId;
    this.company = data.company;
    this.name = data.name;
    this.commerceUser = data.commerceUser;
    this.cost = data.cost;
    this.category = data.category;
    this.note = data.note || '';
    this.items = data.items ? data.items.map((val) => new Item(val)) : [];
    this.expenseType = data.expenseType;
  }

  /**
   * Retrieves expense analytics based on the provided filter criteria.
   * @param {IfilterProps} filter - The filter criteria for analytics
   * @returns {Promise<IAnalyticsResultExpense>} The analytics results
   * @throws {Error} If there's an error fetching the analytics
   */
  static getExpenseAnalytics(filter: IfilterProps) {
    const observer$ = CommerceClient.ehttp
      .makePost<IAnalyticsResultExpense>(`${Expense.baseUrl}/analytics`, filter);

    return lastValueFrom(observer$);
  }

  /**
   * Retrieves all expenses with pagination support.
   * @param {number} [offset=0] - The starting index for pagination
   * @param {number} [limit=20] - The maximum number of records to return
   * @returns {Promise<{ count: number; dataArr: Expense[] }>}
   * An object containing the total count and an array of expense records
   * @throws {Error} If there's an error fetching the expenses
   */
  static async getAll(offset = 0, limit = 20) {
    const query = new URLSearchParams({
      offset: offset.toString(),
      limit: limit.toString()
    }).toString();
    const observer$ = CommerceClient.ehttp
      .makeGet<IdataArrayResponse<TexpenseProps>>(`${Expense.baseUrl}?${query}`);
    const expenses = await lastValueFrom(observer$);

    return {
      count: expenses.count,
      dataArr: expenses.data.map((val) => new Expense(val))
    };
  }

  /**
   * Filters expenses based on specified criteria.
   * @param {IfilterProps} filter - The filter criteria to apply
   * @returns {Promise<{ count: number; dataArr: Expense[] }>}
   * An object containing the total count and an array of filtered expense records
   * @throws {Error} If there's an error applying the filter
   */
  static async filterAll(filter: IfilterProps) {
    const observer$ = CommerceClient.ehttp
      .makePost<IdataArrayResponse<TexpenseProps>>(`${Expense.baseUrl}/filter`, filter);
    const expenses = await lastValueFrom(observer$);

    return {
      count: expenses.count,
      dataArr: expenses.data.map((val) => new Expense(val))
    };
  }

  /**
   * Retrieves a single expense by its ID.
   * @param {string} resourceId - The unique identifier of the expense to retrieve
   * @returns {Promise<Expense>} The requested expense record
   * @throws {Error} If the expense is not found or an error occurs during retrieval
   */
  static async getOne(resourceId: string) {
    const observer$ = CommerceClient.ehttp
      .makeGet<TexpenseProps>(`${Expense.baseUrl}/${resourceId}`);
    const expense = await lastValueFrom(observer$);

    return new Expense(expense);
  }

  /**
   * Creates a new expense record.
   * @param {TexpenseRequest<true>} payload - The expense data to create
   * @returns {Promise<Expense>} The newly created expense record
   * @throws {Error} If the expense creation fails
   */
  static async add(payload: TexpenseRequest<true>) {
    const observer$ = CommerceClient.ehttp
      .makePost<TexpenseProps>(`${Expense.baseUrl}`, payload);

    return new Expense(await lastValueFrom(observer$));
  }

  /**
   * Deletes multiple expenses based on the provided criteria.
   * @param {IbulkDelete} payload - The criteria for identifying which expenses to delete
   * @returns {Promise<IbulkDeleteSuccessResponse>} The result of the bulk delete operation
   * @throws {Error} If the bulk delete operation fails
   */
  static bulkDelete(payload: IbulkDelete) {
    const observer$ = CommerceClient.ehttp
      .makePost<IbulkDeleteSuccessResponse>(`${Expense.baseUrl}/bulk-delete`, payload);

    return lastValueFrom(observer$);
  }

  /**
   * Updates the current expense with new values.
   * @param {TexpenseRequest} payload - The data to update the expense with
   * @returns {Promise<Expense>} The updated expense record
   * @throws {Error} If the update operation fails
   */
  async update(payload: TexpenseRequest) {
    const observer$ = CommerceClient.ehttp
      .makePut<TexpenseProps>(`${Expense.baseUrl}/${this._id}`, payload);

    return new Expense(await lastValueFrom(observer$));
  }

  /**
   * Removes the current expense from the system.
   * @returns {Promise<IdeleteOneResponse | null>} The result of the delete operation
   * @throws {Error} If the deletion fails or the expense doesn't exist
   */
  remove() {
    const observer$ = CommerceClient.ehttp
      .makeDelete<IdeleteOneResponse | null>(`${Expense.baseUrl}/${this._id}`);

    return lastValueFrom(observer$);
  }
}
